import { CheckCircle, CircleOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, CircularProgress, Container, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ReactComponent as LoginImage } from "../../assets/images/login-image.svg";
import MMVDLogoSrc from "../../assets/logos/mmvd-logo.png";
import { useAppDispatch, useAppSelector } from "../../redux";
import {
	clearAuthState,
	useChangeDefaultPasswordMutation,
	useVerifySignupTokenQuery
} from "../../redux/reducers/auth.reducer";
import { VALIDATION_REGEX } from "../../utils/constants";
import { decryptToken } from "../../utils/apiUtils";
import { useNotification } from "../../components/FeedbackNotification/useNotification";

interface IChangePasswordInputs {
	password: string;
	repeat_password: string;
}

interface IPasswordStrength {
	value: number;
	color: string;
	details: ("length" | "case" | "chars")[];
}

const PasswordChange: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const authState = useAppSelector((state) => state.auth);

	const { notify } = useNotification();

	const [showInput, setShowInput] = useState<{ [key in keyof IChangePasswordInputs]: boolean }>({
		password: false,
		repeat_password: false
	});

	const [searchParams] = useSearchParams();

	const paramsToken = useMemo<string>(() => searchParams.get("token") ?? "", [searchParams]);

	// APIS
	// VERIFY SIGNUP TOKEN
	const {
		isLoading: verifySignupTokenLoading,
		isFetching: verifySignupTokenFetching,
		error: verifySignupTokenError,
		data: verifySignupTokenResponse
	} = useVerifySignupTokenQuery(paramsToken, { skip: !paramsToken });

	// CHANGE DEFAULT PASSWORD
	const [changeDefaultPassword, { isLoading: changeDefaultPasswordLoading, error: changeDefaultPasswordError }] =
		useChangeDefaultPasswordMutation();

	const {
		reset,
		watch,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IChangePasswordInputs>();

	const password = watch("password") ?? "";

	const passwordStrength = useMemo<IPasswordStrength>(() => {
		let strength: IPasswordStrength["details"] = [];

		if (password.length >= 8) strength.push("length");
		if (VALIDATION_REGEX.PASSWORD.CASE.test(password)) strength.push("case");
		if (VALIDATION_REGEX.PASSWORD.SPECIAL_CHAR.test(password)) strength.push("chars");

		return {
			value: (strength.length * 100) / 3,
			color:
				strength.length <= 1
					? "var(--color-error-main)"
					: strength.length === 2
					? "var(--color-warning-main)"
					: "var(--color-success-main)",
			details: strength
		};
	}, [password]);

	function handleSubmitForm(formData: IChangePasswordInputs) {
		if (
			(verifySignupTokenResponse && verifySignupTokenResponse.auth_token) ||
			(authState.user.token && authState.user.token !== "invalid")
		) {
			changeDefaultPassword({
				token: verifySignupTokenResponse?.auth_token ?? decryptToken(authState.user.token),
				data: {
					password: formData.password,
					password2: formData.repeat_password
				}
			})
				.unwrap()
				.then(() => {
					notify("Password changed successfully!", "success");
					dispatch(clearAuthState());
					navigate("/login");
				})
				.catch(() => {});
		}
	}

	function handleToggleShowInput(key: keyof IChangePasswordInputs) {
		setShowInput((prev) => ({
			...prev,
			[key]: !prev[key]
		}));
	}

	useEffect(() => {
		reset({
			password: "",
			repeat_password: ""
		});
	}, [reset]);

	useEffect(() => {
		if (verifySignupTokenResponse) {
			dispatch(clearAuthState());
		}
	}, [dispatch, verifySignupTokenResponse]);

	return (
		<Box className="login-component-wrapper">
			<Container maxWidth="xl" sx={{ height: "100%" }}>
				<Box className="login-content-wrapper">
					<Box className="login-image-section">
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1rem" }}>
							{/* <MTESLogo height="100px" /> */}
							<img src={MMVDLogoSrc} alt="MMVD" height="100px" />

							<Box>
								<Typography fontFamily="Patua One" fontWeight={500} fontSize="42px" whiteSpace="nowrap" color="primary">
									MTES
								</Typography>

								<Typography variant="body2" fontFamily="Patua One" fontSize="18px" whiteSpace="nowrap" color="primary">
									Mobile Traffic Enforcement System
								</Typography>
							</Box>
						</Box>

						<LoginImage width="90%" />
					</Box>

					{verifySignupTokenLoading || verifySignupTokenFetching ? (
						<Box className="login-form-section">
							<CircularProgress sx={{ color: "white" }} />
							<Box className="login-form-background" />
						</Box>
					) : verifySignupTokenError ? (
						<Box className="login-form-section">
							<Typography variant="body2" textAlign="center" color="var(--color-error-main)">
								{"data" in verifySignupTokenError && verifySignupTokenError.data
									? String(verifySignupTokenError.data)
									: "Something went wrong"}
							</Typography>

							<Box className="login-form-background" />
						</Box>
					) : (
						<Box className="login-form-section">
							<Typography variant="h4" fontWeight={500}>
								Change Password
							</Typography>

							<Box className="login-form-wrapper" component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
								<Box className="login-input-wrapper">
									<Typography variant="h6" fontWeight={400} color="inherit">
										Password
									</Typography>

									<TextField
										required
										fullWidth
										variant="filled"
										color="primary"
										placeholder="Enter password"
										type={showInput.password ? "text" : "password"}
										autoComplete="off"
										slotProps={{
											input: {
												disableUnderline: true,
												hiddenLabel: true,
												classes: { root: "login-input-root" },
												endAdornment: (
													<InputAdornment position="end">
														<IconButton edge="end" onClick={() => handleToggleShowInput("password")} tabIndex={-1}>
															{showInput.password ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
										error={!!errors.password}
										helperText={errors.password && errors.password.message ? errors.password.message.toString() : ""}
										{...register("password", {
											required: "Password is required",
											pattern: {
												value: VALIDATION_REGEX.PASSWORD.COMPLETE,
												message: "Password should contain at least one alphabet, one number and a special character"
											},
											minLength: {
												value: 8,
												message: "Password should be at least 8 characters long"
											}
										})}
									/>
								</Box>

								<Box className="login-input-wrapper">
									<Typography variant="h6" fontWeight={400} color="inherit">
										Re-enter Password
									</Typography>

									<TextField
										required
										fullWidth
										variant="filled"
										color="primary"
										placeholder="Re-enter password"
										type={showInput.repeat_password ? "text" : "password"}
										autoComplete="off"
										slotProps={{
											input: {
												disableUnderline: true,
												hiddenLabel: true,
												classes: { root: "login-input-root" },
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															edge="end"
															onClick={() => handleToggleShowInput("repeat_password")}
															tabIndex={-1}
														>
															{showInput.repeat_password ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
										error={!!errors.repeat_password}
										helperText={
											errors.repeat_password && errors.repeat_password.message
												? errors.repeat_password.message.toString()
												: ""
										}
										{...register("repeat_password", {
											required: "Repeat password is required",
											validate: (value, { password }) => (value !== password ? "Passwords do not match" : true)
										})}
									/>
								</Box>

								<Box className="login-input-wrapper">
									<Box
										sx={{
											backgroundColor: "var(--color-primary-light)",
											padding: "1rem 1rem",
											borderRadius: "0.25rem"
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<Box sx={{ width: "100%", height: 4, bgcolor: "#E0E0E0", borderRadius: 2, overflow: "hidden" }}>
												<Box
													sx={{
														width: `${passwordStrength.value}%`,
														minWidth: "5%",
														height: 4,
														bgcolor: passwordStrength.color,
														transition: "width ease-out 0.5s"
													}}
												/>
											</Box>
										</Box>

										<Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.5rem" }}>
											{passwordStrength.details.includes("length") ? (
												<CheckCircle color="success" sx={{ fontSize: 14 }} />
											) : (
												<CircleOutlined color="disabled" sx={{ fontSize: 14 }} />
											)}

											<Typography
												variant="body2"
												color={passwordStrength.details.includes("length") ? "success.main" : "text.secondary"}
											>
												At least 8 characters
											</Typography>
										</Box>

										<Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.25rem" }}>
											{passwordStrength.details.includes("case") ? (
												<CheckCircle color="success" sx={{ fontSize: 14 }} />
											) : (
												<CircleOutlined color="disabled" sx={{ fontSize: 14 }} />
											)}

											<Typography
												variant="body2"
												color={passwordStrength.details.includes("case") ? "success.main" : "text.secondary"}
											>
												Include uppercase & lowercase letters
											</Typography>
										</Box>

										<Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.25rem" }}>
											{passwordStrength.details.includes("chars") ? (
												<CheckCircle color="success" sx={{ fontSize: 14 }} />
											) : (
												<CircleOutlined color="disabled" sx={{ fontSize: 14 }} />
											)}

											<Typography
												variant="body2"
												color={passwordStrength.details.includes("chars") ? "success.main" : "text.secondary"}
											>
												Include numbers & special characters
											</Typography>
										</Box>
									</Box>
								</Box>

								{changeDefaultPasswordError ? (
									<Box className="login-error-wrapper">
										<Box className="login-error">
											<Typography variant="body2" textAlign="center" color="var(--color-error-main)">
												{"data" in changeDefaultPasswordError && changeDefaultPasswordError.data
													? String(changeDefaultPasswordError.data)
													: "Failed to change password"}
											</Typography>
										</Box>
									</Box>
								) : null}

								<LoadingButton
									disableElevation
									variant="contained"
									color="success"
									sx={{ paddingY: 1.5, textTransform: "capitalize", marginTop: "1rem" }}
									type="submit"
									loading={changeDefaultPasswordLoading}
								>
									Submit
								</LoadingButton>
							</Box>

							<Box className="login-form-background" />
						</Box>
					)}
				</Box>
			</Container>
		</Box>
	);
};

export default PasswordChange;
